import type { HubCacheSseEvent } from "@/lib/hubCacheChannels";

import type { LaborActiveSessionPayload, LaborSession } from "./laborApi";
import { parseLaborSessionRow } from "./parseLaborSession";

const LABOR_PRESENCE_END_REASONS = new Set([
  "session_ended",
  "idle_timeout",
  "stale_sweep",
]);

function numberFromUnknown(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** True when a hub labor presence SSE event means this persisted session row is no longer open. */
export function laborPresenceEventShouldEndLocalSession(
  event: HubCacheSseEvent | undefined,
  localSessionId: number,
): boolean {
  if (localSessionId <= 0) {
    return false;
  }
  if (event?.labor_reason == null || !LABOR_PRESENCE_END_REASONS.has(event.labor_reason)) {
    return false;
  }
  const eventSessionId = numberFromUnknown(event.labor_session_id);
  if (eventSessionId === null) {
    return true;
  }
  return eventSessionId === localSessionId;
}

/** True when open-session payload from the provider cache or fetch means the local row is stale. */
export function openSessionPayloadEndsLocalSession(
  payload: LaborActiveSessionPayload | undefined,
  localSessionId: number,
): boolean {
  if (localSessionId <= 0 || payload === undefined) {
    return false;
  }
  const openRow = parseLaborSessionRow(payload.session ?? null);
  return openRow === null || openRow.id !== localSessionId;
}

export function laborHeartbeatResponseSessionClosed(response: Record<string, unknown>): boolean {
  return response.updated === false;
}

export function openSessionRowFromPayload(payload: LaborActiveSessionPayload | undefined): LaborSession | null {
  if (payload === undefined) {
    return null;
  }
  return parseLaborSessionRow(payload.session ?? null);
}

export function laborPresenceSessionFromStartedEvent(event: HubCacheSseEvent): LaborSession | null {
  if (event.labor_reason !== "session_started" && event.labor_reason !== "reopened") {
    return null;
  }
  const sessionId = numberFromUnknown(event.labor_session_id);
  const page = typeof event.labor_page === "string" ? event.labor_page.trim() : "";
  if (sessionId == null || sessionId <= 0 || page === "") {
    return null;
  }
  const tabRaw = typeof event.labor_tab_id === "string" ? event.labor_tab_id.trim() : "";
  return {
    id: sessionId,
    page,
    started_at: new Date().toISOString(),
    tab_id: tabRaw === "" ? null : tabRaw,
  };
}

export function laborPresenceEventClearsOpenSession(
  event: HubCacheSseEvent,
  openSessionId: number | null,
): boolean {
  if (event.labor_reason == null || !LABOR_PRESENCE_END_REASONS.has(event.labor_reason)) {
    return false;
  }
  if (openSessionId == null || openSessionId <= 0) {
    return true;
  }
  return laborPresenceEventShouldEndLocalSession(event, openSessionId);
}
